import type { RedisClient } from '../../core/database/redis.js'
import type { QuerySnapshot, DocumentData } from 'firebase-admin/firestore'

export type DependencyStatus = {
  redis: 'connected' | 'error'
  firestore: 'connected' | 'error'
}

type DependencyProbes = {
  checkRedis: () => Promise<string | void>
  checkFirestore: () => Promise<QuerySnapshot<DocumentData> | void>
}

type ProbeOptions = {
  timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 1500

const withTimeout = async <T>(operation: () => Promise<T>, timeoutMs: number): Promise<T> => {
  return await Promise.race([
    operation(),
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), timeoutMs))
  ])
}

/**
 * Probe external dependencies (Redis + Firestore) to determine health.
 * Keeps logic centralized so both /health and /api/health stay consistent.
 */
export async function probeDependencies(
  { checkRedis, checkFirestore }: DependencyProbes,
  options: ProbeOptions = {}
): Promise<DependencyStatus> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  let redisStatus: DependencyStatus['redis'] = 'connected'
  let firestoreStatus: DependencyStatus['firestore'] = 'connected'

  try {
    const result = await withTimeout(checkRedis, timeoutMs)
    if (result && result !== 'PONG') {
      redisStatus = 'error'
    }
  } catch {
    redisStatus = 'error'
  }

  try {
    await withTimeout(checkFirestore, timeoutMs)
  } catch {
    firestoreStatus = 'error'
  }

  return { redis: redisStatus, firestore: firestoreStatus }
}

export function summarizeHealth(status: DependencyStatus) {
  const allHealthy = status.redis === 'connected' && status.firestore === 'connected'
  return {
    status: allHealthy ? 'ok' : 'degraded',
    statusCode: allHealthy ? 200 : 503
  }
}
